import { useEffect, useState } from "react";
import { Crown, Check, Loader2, Zap, Sparkles } from "lucide-react";
import { toast } from "sonner";
import apiClient, { getErrorMessage } from "@/lib/apiClient";
import { isEmbedded, openCheckout } from "@/lib/embed";
import { PageHeader, FullPageLoader, SectionCard } from "@/components/common";

const tierStyle = {
  basic: { color: "#00e5ff", label: "Basic" },
  pro: { color: "#00ff9d", label: "Pro" },
  champ: { color: "#ffb020", label: "Champion" },
  vip: { color: "#8b5cf6", label: "VIP" },
};

export default function Membership() {
  const [plans, setPlans] = useState([]);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(null);

  const load = () => {
    Promise.all([apiClient.get("/memberships/plans"), apiClient.get("/memberships/my")])
      .then(([p, m]) => {
        setPlans(p.data);
        setCurrent(m.data?.subscription ? m.data : null);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const subscribe = async (type) => {
    setSubscribing(type);
    try {
      const { data } = await apiClient.post(`/memberships/subscribe?membership_type=${type}&client_origin=${encodeURIComponent(window.location.origin)}`);
      if (data.url) {
        openCheckout(data.url);
        if (isEmbedded()) {
          toast.info("Complete payment in the new tab, then return here.");
          setSubscribing(null);
        }
      } else {
        toast.error("Could not start checkout");
        setSubscribing(null);
      }
    } catch (err) {
      toast.error(getErrorMessage(err, "Subscription failed"));
      setSubscribing(null);
    }
  };

  if (loading) return <FullPageLoader label="Loading Plans" />;

  const currentType = current?.subscription?.membership_type;

  return (
    <div>
      <PageHeader title="Membership" subtitle="Level up with premium perks, discounts & bonus points." />

      {current?.plan && (
        <SectionCard className="mb-8" style={{ border: "1px solid rgba(139,92,246,0.5)" }}>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: `${(tierStyle[currentType] || tierStyle.vip).color}22`, border: `1px solid ${(tierStyle[currentType] || tierStyle.vip).color}66` }}>
                <Crown className="w-7 h-7 tier-crown" style={{ "--tier-ic": (tierStyle[currentType] || tierStyle.vip).color }} />
              </div>
              <div>
                <p className="text-slate-400 text-xs uppercase tracking-widest">Active Membership</p>
                <h2 className="font-display text-2xl font-bold text-white">{current.plan.name}</h2>
              </div>
            </div>
            <div className="text-right">
              <p className="text-slate-400 text-sm">Renews</p>
              <p className="text-white font-semibold">{current.subscription?.end_date ? new Date(current.subscription.end_date).toLocaleDateString() : "—"}</p>
            </div>
          </div>
        </SectionCard>
      )}

      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-5">
        {plans.map((plan) => {
          const st = tierStyle[plan.type] || tierStyle.basic;
          const isCurrent = currentType === plan.type;
          const featured = !!plan.most_popular;
          return (
            <div key={plan.type}
              className="glass rounded-2xl p-6 flex flex-col relative overflow-hidden"
              style={{ border: featured ? `1px solid ${st.color}88` : "1px solid rgba(120,150,220,0.12)" }}
              data-testid={`plan-${plan.type}`}>
              {featured && (
                <span className="absolute top-4 right-4 chip" style={{ background: `${st.color}22`, border: `1px solid ${st.color}66`, color: st.color }} data-testid={`plan-popular-${plan.type}`}>
                  <Sparkles className="w-3 h-3" /> Most Popular
                </span>
              )}
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: `${st.color}1f`, border: `1px solid ${st.color}55` }}>
                <Crown className="w-6 h-6 tier-crown" style={{ "--tier-ic": st.color }} />
              </div>
              <h3 className="font-display text-xl font-bold text-white">{plan.name}</h3>
              <div className="flex items-end gap-1 mt-2 mb-1">
                <span className="font-display text-4xl font-black" style={{ color: st.color }}>${plan.price}</span>
                <span className="text-slate-400 mb-1.5">/mo</span>
              </div>
              <p className="text-slate-500 text-xs mb-1" data-testid={`plan-tax-note-${plan.type}`}>+ HST at checkout</p>
              <div className="flex items-center gap-2 text-sm text-slate-300 mb-5">
                <Zap className="w-4 h-4" style={{ color: "#ffb020" }} /> {plan.points_multiplier}x points · {plan.discount_percentage}% off
                {plan.monthly_credits > 0 && (
                  <> · <span className="text-white font-semibold">{Number(plan.monthly_credits) * 0.5} hrs/mo</span></>
                )}
              </div>
              <ul className="space-y-2.5 flex-1 mb-6">
                {plan.benefits.map((b, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                    <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: st.color }} /> {b}
                  </li>
                ))}
              </ul>
              <button
                disabled={!!current || subscribing === plan.type || isCurrent}
                onClick={() => subscribe(plan.type)}
                className={isCurrent ? "neon-outline-btn w-full py-3.5" : "neon-btn w-full py-3.5"}
                data-testid={`subscribe-${plan.type}`}>
                {subscribing === plan.type ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting...</>
                  : isCurrent ? "Current Plan"
                  : current ? "Active Plan Exists"
                  : "Subscribe"}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
